import { AMPLITUDE_KEY, AMPLITUDE_REPLAY_SAMPLE_RATE } from '../settings';

let ampPromise: Promise<any> | null = null;
const getAmp = () => (ampPromise ??= import('@amplitude/analytics-browser'));

// Session Replay is a separate, heavy bundle. Loaded lazily AND only when
// replay is actually on, so a deploy with the sample rate at 0 never pays for
// it.
let replayPromise: Promise<any> | null = null;
const getReplayPlugin = () =>
  (replayPromise ??= import('@amplitude/plugin-session-replay-browser'));

// Stable name the SDK registers the plugin under — needed to remove it.
const REPLAY_PLUGIN_NAME = '@amplitude/plugin-session-replay-browser';

let replayAttached = false;

export const initializeAmplitude = (userId: string) => {
  if (!AMPLITUDE_KEY) return;
  getAmp().then(({ init }: any) =>
    init(AMPLITUDE_KEY, userId, {
      // Pinned, and not a preference: SDK v2 turns autocapture ON by default,
      // where v1 had it off. Without this the upgrade alone would start
      // emitting page views, form interactions and file downloads into the
      // same Amplitude project — new event volume nobody asked for, mixed in
      // with the named events this dashboard actually reports.
      defaultTracking: {
        sessions: true,
        pageViews: false,
        formInteractions: false,
        fileDownloads: false,
      },
    })
  );
};

/**
 * Attach or detach Session Replay at runtime, so recording is scoped to a
 * screen instead of the whole dashboard.
 *
 * Once attached the plugin records the SESSION, not the route — so limiting it
 * to one screen means adding the plugin on entry and removing it on exit
 * (removal runs the plugin's teardown, which stops the recorder). Mirrors the
 * agents dashboard, where replay is scoped to /agents the same way.
 *
 * No-op when the key is missing or the sample rate is 0, and idempotent.
 */
export const setReplayEnabled = (enabled: boolean) => {
  if (!AMPLITUDE_KEY || AMPLITUDE_REPLAY_SAMPLE_RATE <= 0) return;
  if (enabled === replayAttached) return;

  replayAttached = enabled;

  getAmp().then(async (amp: any) => {
    try {
      if (enabled) {
        const { sessionReplayPlugin } = await getReplayPlugin();

        // The user may have left the screen while this chunk downloaded.
        if (!replayAttached) return;

        amp.add(
          sessionReplayPlugin({
            sampleRate: AMPLITUDE_REPLAY_SAMPLE_RATE,
            privacyConfig: {
              // 'light' masks what is never safe to capture (passwords, cards,
              // phone, email) and leaves ordinary fields readable — which is
              // the point of watching someone fill this form.
              defaultMaskLevel: 'light',
              // Except the environment variable values, which are secrets and
              // are NOT covered by 'light': the field is a password input only
              // until the user clicks the eye to reveal it, and a revealed
              // secret would otherwise be recorded in full. Keyed on the input
              // name (`env-value-<id>`), so it holds in both states.
              maskSelector: ['input[name^="env-value-"]'],
            },
          })
        );
      } else {
        amp.remove(REPLAY_PLUGIN_NAME);
      }
    } catch (e) {
      // Replay is best-effort and must never break the product or analytics.
    }
  });
};

export const trackEvent = (name: string, data?: Record<string, any>) => {
  if (!AMPLITUDE_KEY) return;
  getAmp().then(({ track }: any) => track(name, data));
};

export const setUserForTracking = (id: string, name?: string) => {
  if (!AMPLITUDE_KEY) return;

  getAmp().then(({ identify, Identify }: any) => {
    const identifyObj = new Identify();
    identifyObj.setOnce('id', id);
    identifyObj.setOnce('name', name as string);
    identify(identifyObj);
  });
};

export const AmplitudeEvent = {
  AT_CONTAINERS_DASHBOARD_PAGE: 'At Dashboard Containers page',
  AT_SELECT_REPOSITORY_PAGE: 'At Select Repository page',
  AT_CREATE_APP_PAGE: 'At Create App page',
  AT_APP_OVERVIEW_PAGE: 'At App Overview page',
  AT_APP_DEPLOYMENT_PAGE: 'At App Deployment page',
  AT_APP_LOGS_PAGE: 'At App Logs page',
  AT_APP_METRICS_PAGE: 'At App Metrics page',
  AT_APP_SETTINGS_PAGE: 'At App Settings page',
  EDIT_GITHUB_PERMISSION_CLICK: 'Edit Github Permission Click',
  IMPORT_GITHUB_REPOSITORY_CLICK: 'Import github repository Click',
  CREATE_APP_CLICK: 'Create APP Click',
  APP_SETTINGS_SAVE_CLICK: 'App Settings Save Click',
  DEPLOY_LATEST_COMMIT: 'deploy latest commit',
  CLICK_ON_DELETE_APP: 'click on delete app',
  CLICK_ON_CREATE_DOMAIN_BTN: 'click on add custom domain button',
  AT_AGENT_UI: 'Agent - At AI Agent',
  AGENT_CREATE_ACTION: 'Agent - Created new Agent',
  AGENT_PROMPT_SELECTED: 'Agent - Prompt Selected',
  AGENT_PROMPT_USED: 'Agent - Submited new prompt',
  AGENT_EDIT_NAME: 'Agent - Edit Agent Name',
  AGENT_DELETE: 'Agent- Delete Agent',
  PUBLISH_MODAL_OPENED: 'Publish Modal Opened',
  PUBLISH_UPGRADE_CLICKED: 'Publish Upgrade Clicked',
  PUBLISH_SUBDOMAIN_SAVE_CLICKED: 'Publish Subdomain Save Clicked',
  PUBLISH_SUCCESS: 'Publish Success',
  PUBLISH_ERROR: 'Publish Error',
  PUBLISH_MODAL_CLOSED: 'Publish Modal Closed',
  AGENT_APP_MODAL_PUBLISH_OPEN: 'Agent App Modal Publish Open',
  AGENT_APP_PUBLISH_PAY_CLICK: 'Agent App Modal Publish Pay Click',
  AGENT_APP_PUBLISH_PAY_SUCCESS: 'Agent App Modal Publish Payment Success',
  AGENT_PLAN_MODAL_OPEN: 'Agent Plan Modal Open',
  AGENT_PLAN_MODAL_PAY_CLICK: 'Agent Plan Modal Pay Click',
  AGENT_PLAN_MODAL_PAY_SUCCESS: 'Agent Plan Modal Payment Sucess',
  AGENT_APP_FREE_PUBLISH_FLOW: 'Agent App Free Publish Flow',
};